import * as React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { SEO } from '@/components/global/SEO';
import Page from '@/components/v2/Page';
import { V1PageWrapper } from '@/components/v2/Page/V1PageWrapper';

import {
  Button,
  GradientHeader,
  Text,
  Voronoi
} from '../../../components/design-system';
import { CustomerBanner } from '../../../components/global/CustomerBanner';
import { SocialTout } from '../../../components/global/SocialTout';
import { UseCasesTout } from '../../../components/global/UseCasesTout';
import { ForTeamsTout } from '../../../components/notebooks/ForTeamsTout';
import { NewWayToNotebookTout } from '../../../components/notebooks/NewWayToNotebookTout';
import { ReactiveExecutionTout } from '../../../components/notebooks/ReactiveExecutionTout';
import { ANIMATIONS, INTERNAL_LINKS, SIGNUP_LINK } from '../../../utils';
import { SOCIAL_SHARE } from '../../../utils/social-share-images';

const Wrapper = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const TextHero = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 70px 40px 60px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 30px 40px 40px;
  }
`;

const StyledHeading = styled(GradientHeader)`
  && {
    text-align: center;
    margin-bottom: 20px;
    padding: 0 20px;
    max-width: 900px;
  }
`;

const HeroDescription = styled(Text).attrs({ size: 'extra-large' })`
  max-width: 700px;

  && {
    text-align: center;

    margin-bottom: 45px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    gap: 10px;
    flex-direction: column;
    align-self: stretch;
  }
`;

const INFO = {
  Title: "So much more than the world's best notebook",
  Description:
    "Code, no-code, and queries live together in a flexible, collaborative canvas. It's the complete set of building blocks for your most important data work."
};

const NotebooksPage: React.ComponentType = React.memo(() => {
  return (
    <Page
      seo={
        <SEO
          description={INFO.Description}
          image={SOCIAL_SHARE.Notebooks}
          title={INFO.Title}
          url={INTERNAL_LINKS.Notebooks}
        />
      }>
      <V1PageWrapper>
        <Voronoi muted={false} />
        <Wrapper {...ANIMATIONS.SimpleFadeIn}>
          <TextHero>
            <StyledHeading $purple={true} renderAs="h0">
              {INFO.Title}
            </StyledHeading>
            <HeroDescription>{INFO.Description}</HeroDescription>
            <Actions>
              <Button
                link={SIGNUP_LINK}
                size="large"
                stylez="loud"
                text="Get started for free"
              />
              <Button
                link={INTERNAL_LINKS.RequestADemo}
                size="large"
                text="Request a demo"
              />
            </Actions>
          </TextHero>
          <CustomerBanner minimal={true} />
          <NewWayToNotebookTout />
          <ReactiveExecutionTout />
          <ForTeamsTout />
          <SocialTout
            customerQuotes={[
              {
                author: 'Erik Selin',
                authorAvatarPath:
                  'https://images.ctfassets.net/mmgv7yhaaeds/2CRbfZ6XZxgEzIPS3nKliw/355040448661e0e5ecf47d3d59bee1ff/erik-selin-headshot.jpeg',
                authorTitle: 'Engineering Director, Data Platform',
                customerName: 'Doximity',
                logoPath:
                  'https://images.ctfassets.net/mmgv7yhaaeds/3PjusBCgUrsFocjuOmgqiw/1fc8db377bfcdae048c8b9de24c844b1/Doximity.svg',
                quote:
                  'With Hex and Snowflake, Doximity was able to completely transition off of Jupyter Notebooks.'
              }
            ]}
            includeG2Proof={true}
          />
          <UseCasesTout description="Get started with examples of our most powerful notebooks." />
        </Wrapper>
      </V1PageWrapper>
    </Page>
  );
});

export default NotebooksPage;
